import { Outlet, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { Suspense, useEffect, useState } from "react";
import { getScheduleItems } from "../features/calendar/scheduleItem.thunk";
import { getOrganizations } from "../features/organization/organization.thunk";

import { getAPIIntegrations, getMe } from "../features/users/users.thunk";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { initializeApp } from "../services/appRegistry";
import { AppShellSkeleton } from "./app-shell/AppShellSkeleton";
import { getDevices, getMySettings } from "../features/settings/settings.thunk";
import {
  getActiveRecording,
  getCompletedTasks,
  initializeTasks,
} from "../features/tasks/taskThunk";
import { getCollections } from "../features/tasks/collection/collection.thunk";
import {
  getProjects,
  getProjectTasks,
} from "../features/tasks/project/project.thunk";
import { isDemoMode } from "../app/demoMode";
import NativeTaskPlayerSync from "../components/native/NativeTaskPlayerSync";
import NativeDeviceCalendarSync from "../components/native/NativeDeviceCalendarSync";
import { isNativeApp, supportsNativeFeature } from "../services/nativeBridge";
import { cacheUserId, restoreCache } from "../app/offlineCache";

const PrivateRoute = () => {
  const { loggedIn, checkingStatus } = useAuthStatus(); // ✅ Reuse the hook
  const dispatch = useAppDispatch();
  const { authData } = useSelector((state: RootState) => state.auth); // Ensure you are accessing the correct slice
  const userId = useSelector(cacheUserId);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        if (isDemoMode()) {
          // Loaded only for the demo, so it stays out of the start bundle
          const [{ default: MockAdapter }, { demoApiClient }] =
            await Promise.all([
              import("axios-mock-adapter"),
              import("../app/demoApiClient"),
            ]);
          const mock = new MockAdapter(demoApiClient, { delayResponse: 300 });

          // Gesperrt: Team-/Account-Verwaltung
          mock.onAny(/\/users\/integrations/).reply(403, {
            code: "DEMO_LOCKED",
            message: "Diese Funktion erfordert einen echten Account.",
          });

          // Fallback für alles Unbenannte — bewusst 200/{} statt passThrough,
          // da ohne echtes Token ein passThrough nur ins 401 läuft
        }
        if (!isInitialized && loggedIn) {
          // Only once if not initialized and logged in
          // The last data of the user shows at once, the current data follows
          const fromCache = await restoreCache(dispatch, userId);
          // Creates the sockets right away, connecting takes a round trip
          const sockets = initializeApp(dispatch);
          // Offline the sockets only give up after their timeout
          if (fromCache && !navigator.onLine) setIsInitialized(true);
          await sockets;
          if (fromCache) setIsInitialized(true);

          // Loaded in the background: the start screen doesn't wait for them
          // (see PrivateRoute.startup.test.tsx)
          dispatch(getOrganizations());
          dispatch(getDevices());
          dispatch(getAPIIntegrations());
          dispatch(getProjectTasks());
          dispatch(getProjects({}));
          dispatch(getCollections({}));
          dispatch(getCompletedTasks());

          // What the start screen shows - all independent, in parallel
          await Promise.all([
            dispatch(initializeTasks()),
            dispatch(getScheduleItems()),
            dispatch(getMe()),
            dispatch(getMySettings()),
            dispatch(getActiveRecording()),
          ]);

          console.log("%c✅Initialization complete", "color: green; ");
          setIsInitialized(true);
          // ✅ Mark as initialized only after everything is ready, because then other components are loaded which might use the socket
        }
      } catch (err) {
        console.error("Error during initialization:", err);
      }
    };
    init();
  }, [authData, loggedIn]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!loggedIn && !checkingStatus) {
      console.log("%c🚫 Not logged in, redirecting to login", "color: red; ");
      navigate("/login");
    }
  }, [loggedIn, checkingStatus]);

  if (checkingStatus || (loggedIn && !isInitialized)) {
    // ✅ Show skeleton during auth check or app init

    console.log("Waiting");
    return <AppShellSkeleton />;
  }

  return (
    <Suspense fallback={<AppShellSkeleton />}>
      {isNativeApp() && <NativeTaskPlayerSync />}
      {supportsNativeFeature("deviceCalendar") && <NativeDeviceCalendarSync />}
      <Outlet />
    </Suspense>
  );
};

export default PrivateRoute;
